import { isMasked } from "./format";
import type { components } from "../api/schema";

type Week = components["schemas"]["Insights"]["weeks"][number];

/**
 * Claims and vouches per week as two small lines, drawn without numbers (the shape is the point;
 * the figures are in the other views). While the week replays, the lines draw themselves over the
 * replay's 60 seconds; otherwise they draw in quickly. A masked week is a gap.
 */
export function WeeklyLine({ weeks, replaying }: { weeks: Week[]; replaying: boolean }) {
  const W = 300, H = 70, P = 4;
  const r1 = (n: number) => Math.round(n * 10) / 10;
  const val = (c: Week["claims"]) => (isMasked(c) ? null : c.count);
  const max = Math.max(1, ...weeks.flatMap((w) => [val(w.claims) ?? 0, val(w.vouches) ?? 0]));
  const x = (i: number) => P + (weeks.length < 2 ? (W - 2 * P) / 2 : (i * (W - 2 * P)) / (weeks.length - 1));
  const y = (v: number) => H - P - (v / max) * (H - 2 * P);
  const path = (pick: (w: Week) => Week["claims"]) => weeks.map((w, i) => {
    const v = val(pick(w));
    return v === null ? "" : `${i === 0 || val(pick(weeks[i - 1])) === null ? "M" : "L"}${r1(x(i))},${r1(y(v))}`;
  }).join("");
  const dur = replaying ? "60s" : "1.2s";
  if (!weeks.length) return <p className="hint">Nothing yet.</p>;
  return (
    <figure className="ins-weeks" data-replaying={replaying ? "true" : "false"}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Claims and vouches per week" key={`${replaying}`}>
        <path d={path((w) => w.vouches)} className="ins-line vouches" pathLength={1} style={{ animationDuration: dur }} data-testid="weeks-vouches" />
        <path d={path((w) => w.claims)} className="ins-line claims" pathLength={1} style={{ animationDuration: dur }} data-testid="weeks-claims" />
      </svg>
      <figcaption className="hint"><span className="ins-swatch claims" /> claims <span className="ins-swatch vouches" /> vouches · {weeks[0].week} to {weeks[weeks.length - 1].week}</figcaption>
    </figure>
  );
}
